/**
 * Parses admin-pasted JSON (e.g. exported from Firestore / tools) into plain fields
 * so callers can build a Firestore DocumentReference with doc(db, ...).
 */

export type PlainPastedLocationPhotoFields = {
  path: string;
  campPhotoCollection: string;
  campPhotoDocumentId: string;
  photoReference: string;
  yOffset: number;
  caption?: string;
  altText?: string;
  permissionStatus?: "seeking" | "granted";
  ogPath?: string;
  permissionNote?: string;
  permissionInternalNote?: string;
  order?: number;
};

/** Resolves pasted document reference shapes to [collectionId, documentId]. */
export function extractFirestoreDocumentSegments(raw: unknown): [string, string] | null {
  if (raw == null) return null;

  if (typeof raw === "object" && raw !== null && "referencePath" in raw) {
    const path = String((raw as { referencePath: string }).referencePath).trim();
    const segs = path.split("/").filter(Boolean);
    if (segs.length >= 2) return [segs[0], segs[1]];
    return null;
  }

  if (typeof raw === "string") {
    const segs = raw.split("/").filter(Boolean);
    if (segs.length >= 2) return [segs[0], segs[1]];
    if (segs.length === 1 && segs[0]) return ["campPhotos", segs[0]];
  }

  return null;
}

export function parsePastedLocationPhotoJson(text: string): PlainPastedLocationPhotoFields {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Invalid JSON");
  }

  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Photo JSON must be a single object");
  }

  const obj = parsed as Record<string, unknown>;

  const path = typeof obj.path === "string" ? obj.path.trim() : "";
  if (!path) {
    throw new Error('Photo JSON must include a non-empty string "path"');
  }

  const refSegs = extractFirestoreDocumentSegments(obj.campPhotoId);
  if (!refSegs) {
    throw new Error(
      'Photo JSON must include a valid "campPhotoId" (e.g. { "referencePath": "campPhotos/..." } or a DocumentReference)'
    );
  }

  const yOffset =
    typeof obj.yOffset === "number" && !Number.isNaN(obj.yOffset) ? obj.yOffset : 0;
  const photoReference = typeof obj.photoReference === "string" ? obj.photoReference : "";

  const out: PlainPastedLocationPhotoFields = {
    path,
    campPhotoCollection: refSegs[0],
    campPhotoDocumentId: refSegs[1],
    photoReference,
    yOffset,
  };

  if (typeof obj.caption === "string") {
    out.caption = obj.caption;
  }
  if (typeof obj.altText === "string") {
    out.altText = obj.altText;
  }
  if (obj.permissionStatus === "seeking" || obj.permissionStatus === "granted") {
    out.permissionStatus = obj.permissionStatus;
  }
  if (typeof obj.ogPath === "string" && obj.ogPath.trim().length > 0) {
    out.ogPath = obj.ogPath.trim();
  }
  if (typeof obj.permissionNote === "string" && obj.permissionNote.length > 0) {
    out.permissionNote = obj.permissionNote;
  }
  if (typeof obj.permissionInternalNote === "string" && obj.permissionInternalNote.length > 0) {
    out.permissionInternalNote = obj.permissionInternalNote;
  }
  if (typeof obj.order === "number" && !Number.isNaN(obj.order)) {
    out.order = obj.order;
  }

  return out;
}
